import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Building2,
  Users,
  TrendingDown,
  TrendingUp,
  LayoutGrid,
  ArrowUpRight,
  ArrowRight,
  Phone,
  CheckCircle2,
  MapPin,
} from "lucide-react";
import { whyMarseille, companyInfo, marseilleImage } from "../data/mockData";

const iconMap = { Building2, Users, TrendingDown, TrendingUp, LayoutGrid, ArrowUpRight };

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const InvestirPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0">
          <img
            src={marseilleImage}
            alt="Marseille"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1A1A2E]/70" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl text-white"
          >
            <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
              {"Investir à Marseille"}
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold mt-4">
              Pourquoi{" "}
              <span className="text-[#C4956A]">Marseille</span> ?
            </h1>
            <p className="text-lg text-white/70 mt-6 leading-relaxed">
              {"Marseille offre un équilibre rare entre dynamique de valorisation, profondeur de marché et accessibilité. Une métropole en pleine transformation."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Marseille Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A2E]">
                {"6 raisons d'investir à Marseille"}
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.isArray(whyMarseille) ? whyMarseille.map((item, i) => {
              const Icon = iconMap[item.icon];
              return (
                <AnimatedSection key={i}>
                  <div className="p-8 bg-[#FAF9F6] rounded-2xl hover:shadow-lg hover:shadow-[#C4956A]/5 transition-all duration-300 border border-transparent hover:border-[#E8DDD3] h-full">
                    <div className="w-14 h-14 rounded-xl bg-[#C4956A]/10 flex items-center justify-center mb-5">
                      {Icon && <Icon className="w-6 h-6 text-[#C4956A]" />}
                    </div>
                    <h3 className="text-lg font-semibold text-[#1A1A2E] mb-3">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#1A1A2E]/60 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </AnimatedSection>
              );
            }) : null}
          </div>
        </div>
      </section>

      {/* Market Insights */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-[#1A1A2E]">
                  {"Le marché immobilier marseillais"}
                </h2>
                <p className="text-[#1A1A2E]/60 leading-relaxed">
                  {"Le marché immobilier à Marseille montre une tendance à la hausse des prix, signe d'un marché dynamique et en croissance."}
                </p>
                <div className="space-y-4">
                  {[
                    "Prix immobiliers plus abordables que les autres grandes métropoles",
                    "Forte demande locative entre étudiants, jeunes actifs et familles",
                    "Projets de rénovation urbaine en cours (Euroméditerranée)",
                    "Potentiel de valorisation immobilière prometteur",
                    "Quartiers en pleine transformation : Le Panier, La Joliette",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#C4956A] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#1A1A2E]/70">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "880K+", label: "Habitants" },
                  { value: "80K+", label: "Étudiants" },
                  { value: "~7%", label: "Rendement moyen" },
                  { value: "3 200€", label: "Prix moyen/m²" },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className="p-6 bg-white rounded-2xl text-center shadow-sm"
                  >
                    <p className="text-2xl font-bold text-[#C4956A]">
                      {stat.value}
                    </p>
                    <p className="text-sm text-[#1A1A2E]/50 mt-1">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A2E]">
                {"Les quartiers à fort potentiel"}
              </h2>
              <p className="text-[#1A1A2E]/60 mt-4">
                {"Notre expertise locale nous permet d'identifier les meilleurs emplacements pour votre investissement."}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: "Le Panier",
                desc: "Quartier historique en pleine renaissance. Fort potentiel de valorisation avec une demande locative croissante.",
                type: "Patrimonial",
              },
              {
                name: "La Joliette / Euroméd",
                desc: "Quartier d'affaires en plein essor. Idéal pour les jeunes actifs et professionnels en mobilité.",
                type: "Rendement",
              },
              {
                name: "Cinq Avenues / Longchamp",
                desc: "Secteur résidentiel recherché, parfait pour la location meublée longue durée et la colocation.",
                type: "Sécurisé",
              },
              {
                name: "Les Réformés / Camas",
                desc: "Quartier branché et vivant, très prisé des étudiants et jeunes actifs. Excellente desserte transports.",
                type: "Étudiant",
              },
              {
                name: "Vieux Port",
                desc: "Cœur historique de Marseille. Biens de caractère avec un fort attrait touristique et locatif.",
                type: "Patrimonial",
              },
              {
                name: "Castellane / Prado",
                desc: "Quartier bourgeois avec une forte demande familiale. Investissement sécurisé et valorisation stable.",
                type: "Sécurisé",
              },
            ].map((q, i) => (
              <AnimatedSection key={i}>
                <div className="p-6 bg-[#FAF9F6] rounded-2xl hover:shadow-lg hover:shadow-[#C4956A]/5 transition-all duration-300 border border-transparent hover:border-[#E8DDD3]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#C4956A]" />
                      <h3 className="font-semibold text-[#1A1A2E]">{q.name}</h3>
                    </div>
                    <span className="px-3 py-1 bg-[#C4956A]/10 rounded-full text-xs font-medium text-[#C4956A]">
                      {q.type}
                    </span>
                  </div>
                  <p className="text-sm text-[#1A1A2E]/60 leading-relaxed">
                    {q.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="bg-[#1A1A2E] rounded-3xl p-12 lg:p-16 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="space-y-4 text-center lg:text-left">
                <h2 className="text-3xl lg:text-4xl font-bold text-white">
                  {"Prêt à investir à"}{" "}
                  <span className="text-[#C4956A]">Marseille</span> ?
                </h2>
                <p className="text-white/60 max-w-md">
                  {"Prenez rendez-vous avec notre équipe pour étudier les meilleures opportunités."}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C4956A] text-white font-medium rounded-lg hover:bg-[#B38459] transition-all"
                >
                  Prendre rendez-vous
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 transition-all"
                >
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone}
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default InvestirPage;
